import baseApi, { fileUploadBaseQuery } from '@/modules/services/base';
import { TemplateTag, TemplatesTag } from '@/modules/services/tags';
import {
  GetTemplatesParams,
  TemplateDataType,
  TemplateFormDataType,
} from '@/modules/types/template';
import { DeleteResponse } from '../types/choice';
import { PaginationType } from '../types/elearning';

const templatesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTemplates: build.query<
      PaginationType<TemplateDataType>,
      GetTemplatesParams
    >({
      query: (params) => ({
        url: 'templates',
        params,
      }),
      providesTags: [TemplatesTag],
    }),
    getFavouriteTemplates: build.query<TemplateDataType[], void>({
      query: () => ({
        url: 'templates/favourites',
      }),
      providesTags: [TemplatesTag],
    }),
    getTemplate: build.query<TemplateDataType, string>({
      query: (templateId) => ({
        url: `templates/${templateId}`,
      }),
      providesTags: [TemplateTag],
    }),
    updateTemplate: build.mutation<TemplateDataType, TemplateDataType>({
      query: (data) => ({
        url: `templates/${data.id}`,
        method: 'PUT',
        body: data,
        responseType: 'json',
      }),
      invalidatesTags: [TemplateTag, TemplatesTag],
    }),
    createTemplate: build.mutation<TemplateDataType, TemplateFormDataType>({
      query: (data) => ({
        url: `templates`,
        method: 'POST',
        body: data,
        responseType: 'json',
      }),
      invalidatesTags: [TemplateTag, TemplatesTag],
    }),
    deleteTemplate: build.mutation<DeleteResponse, string>({
      query: (id) => ({
        url: `templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TemplateTag, TemplatesTag],
    }),
    uploadTemplateImage: build.mutation<
      { fileName: string },
      { file: File; name: string }
    >({
      async queryFn({ file, name }, api, extraOptions) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('name', name);

        const result = await fileUploadBaseQuery(
          {
            url: 'templates/upload-image',
            method: 'POST',
            body: formData,
          },
          api,
          extraOptions,
        );

        return result as any;
      },
      invalidatesTags: [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTemplatesQuery,
  useGetFavouriteTemplatesQuery,
  useGetTemplateQuery,
  useUpdateTemplateMutation,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
  useUploadTemplateImageMutation,
} = templatesApi;

export default templatesApi;
